/* eslint-disable react-hooks/exhaustive-deps */
import { forgotPasswordAction, newPasswordAction } from 'actions/auth';
import { Button, Form, Input, notification, Typography, Spin } from 'antd';
import { useAppDispatch } from 'app/hooks';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EMAIL_NOT_EXIST, isEmail, LINK_EXPIRED, TOKEN_INVALID } from 'utils';
import {
  IForgotPasswordFormValues,
  IForgotPasswordProps,
  IPayloadForgotPassword,
} from '../interfaces';
import PopupOk from './PopupOk';
import Wrapper from './Wrapper';
import { API_URL } from 'config';
import axios from 'axios';
import { InfoCircleFilled, LeftOutlined } from '@ant-design/icons';
import { translate } from 'libs';

const ForgotPassword = ({ isCreateNewPassword = false }: IForgotPasswordProps) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { search } = useLocation();
  const queryString = new URLSearchParams(search);

  const token: string = queryString.get('token') || '';
  const emailQs: string = queryString.get('email') || '';
  const typeQs: string = queryString.get('type') || '';
  const isTypeSetNew: boolean = typeQs === 'SET_NEW';
  const [isLoadingPage, setIsLoadingPage] = React.useState<boolean>(false);

  const [isShowPopup, setIsShowPopup] = React.useState<boolean>(false);

  // const isLoading: boolean = useAppSelector((state) => state.auth.isLoading);

  const checkToken = React.useCallback(async () => {
    setIsLoadingPage(true);
    const queryString = new URLSearchParams(search);
    const token: string | null = queryString.get('token');

    try {
      await axios.get(
        `${API_URL}/admin/check-token?token=${token}&token_type=${
          isTypeSetNew ? 'INVITE_ADMIN_USER' : 'FORGOT'
        }`
      );
    } catch (error: any) {
      if (error) {
        const { response } = error;
        if (response?.status === 400) {
          if (response?.data?.error_code === LINK_EXPIRED) {
            return history.push({
              pathname: '/admin/link-expired',
            });
          }
        }
      } else {
        notification.error({
          message: 'Internal Server Error',
        });
      }
    } finally {
      setIsLoadingPage(false);
    }
  }, []);

  React.useEffect(() => {
    return () => {
      setIsShowPopup(false);
    };
  }, []);

  React.useEffect(() => {
    if (token) {
      checkToken();
    }
  }, []);

  const onForgotPasswordError = (errorCode: string) => {
    switch (errorCode) {
      case EMAIL_NOT_EXIST:
        if (isCreateNewPassword) {
          // notification.error({ message: t('loginPage.emailNotExist') });
          history.push({
            pathname: '/admin/link-expired',
          });
          break;
        }
        form.setFields([
          {
            name: 'email',
            errors: [translate('loginPage.emailNotExist')],
          },
        ]);
        break;
      case TOKEN_INVALID:
        notification.error({ message: translate('fgpwPage.tokenExpired') });
        break;
      default:
        break;
    }
  };

  const onFinish = ({ email, new_password, confirm_password }: IForgotPasswordFormValues) => {
    const payload: IPayloadForgotPassword = {
      values: { email },
      onSuccess: () => {
        setIsShowPopup(true);
      },
      onError: (errorCode: string) => {
        console.log('🚀 ~ errorCode', errorCode);

        onForgotPasswordError(errorCode);
      },
    };

    if (!isCreateNewPassword) {
      return dispatch(forgotPasswordAction(payload));
    }

    /**
     * Create new password
     */

    payload.values = {
      email: emailQs,
      token,
      new_password,
      confirm_password,
      is_create: isTypeSetNew,
    };

    return dispatch(newPasswordAction(payload));
  };

  const handleReturnLogin = () => history.push('/auth/login');

  return (
    <Spin spinning={isLoadingPage} size="small">
      <Wrapper>
        {isShowPopup ? (
          <PopupOk
            title={
              isCreateNewPassword
                ? translate(`fgpwPage.${isTypeSetNew ? 'newPwCreated2' : 'newPwSet'}`)
                : translate('fgpwPage.pwLinkSent')
            }
            description={!isCreateNewPassword ? translate('fgpwPage.checkYourInbox') : ''}
            isReturnLogin
            icon="/images/ic-checked.png"
            handleOk={handleReturnLogin}
          />
        ) : (
          <div className="forgot-pw">
            <Button style={{}} onClick={() => history.push('/auth/login')}>
              <LeftOutlined />
              {translate('button.Back.title')}
            </Button>
            <div className="two-fac-icon">
              <img src="/images/ic-forgot-pw.png" alt="Forgot password" />
            </div>

            <div className="txt-title-main">
              <Typography.Text className="txt-title">
                {isCreateNewPassword
                  ? translate(`fgpwPage.${isTypeSetNew ? 'setPassword' : 'createNewPw'}`)
                  : translate('fgpwPage.forgotPassword')}
              </Typography.Text>
            </div>

            {!isCreateNewPassword && (
              <div className="description">
                <Typography.Text className="txt-description">
                  {translate('fgpwPage.alert')}
                </Typography.Text>
              </div>
            )}

            <Form
              onFinish={onFinish}
              layout="vertical"
              className={isCreateNewPassword ? 'form-new-pw' : ''}
              form={form}
              scrollToFirstError
              autoComplete="off"
            >
              {isCreateNewPassword ? (
                <div>
                  <Form.Item
                    label={
                      isTypeSetNew
                        ? translate('loginPage.password')
                        : translate('fgpwPage.newPassword')
                    }
                    name="new_password"
                    rules={[
                      {
                        required: true,
                        message: isTypeSetNew
                          ? translate('loginPage.passwordRequired')
                          : translate('fgpwPage.newPasswordRequired'),
                      },
                      {
                        pattern: new RegExp(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z]{8,}$/),
                        message: translate('fgpwPage.password.wrong_format'),
                      },
                    ]}
                    tooltip={{
                      title: `${translate('loginPage.passwordTooltip')}`,
                      icon: <InfoCircleFilled />,
                    }}
                  >
                    <Input.Password
                      size="large"
                      placeholder={translate('fgpwPage.passwordPlaceholder')}
                      className="input-password"
                      maxLength={16}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      isTypeSetNew
                        ? translate('fgpwPage.reEnterSetPassword')
                        : translate('fgpwPage.reEnterNewPassword')
                    }
                    name="confirm_password"
                    rules={[
                      { required: true, message: translate('fgpwPage.reEnterNewPasswordRequired') },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('new_password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(translate('fgpwPage.twoPwNotMatch')));
                        },
                      }),
                    ]}
                    dependencies={['new_password']}
                  >
                    <Input.Password
                      size="large"
                      placeholder={translate('fgpwPage.reEnterNewPasswordPlaceholder')}
                      className="input-password"
                      maxLength={16}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  label={translate('fgpwPage.email')}
                  name="email"
                  rules={[
                    { required: true, message: translate('loginPage.emailRequired') },
                    () => ({
                      validator(_rule, value) {
                        if (!value) return Promise.reject();

                        const checkEmail: boolean = isEmail(value);

                        if (checkEmail) {
                          return Promise.resolve();
                        }

                        return Promise.reject(new Error(translate('loginPage.emailFormat')));
                      },
                    }),
                  ]}
                >
                  <Input
                    size="large"
                    placeholder={translate('loginPage.emailPlaceholder')}
                    allowClear
                    className="input-email"
                    maxLength={256}
                  />
                </Form.Item>
              )}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  className="btn-rs-link"
                  // loading={isLoading}
                >
                  {isCreateNewPassword
                    ? translate(`fgpwPage.${isTypeSetNew ? 'setPassword' : 'setNewPassword'}`)
                    : translate('fgpwPage.sendResetLink')}
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
      </Wrapper>
    </Spin>
  );
};

export default ForgotPassword;
